import React, { useEffect, useState } from 'react';
import Button from '../UI/Button';
import Input from '../UI/Input';
import { InterviewerItem } from '../../types';

export type MentorProfile = {
  mentorId?: number;
  fullName?: string;
  contactEmail?: string;
  phoneNumber?: string;
  currentJobTitle?: string;
  yearsOfExperience?: number;
  primaryExpertiseArea?: string;
  technicalSkills?: string;
  aiSkills?: string | null;
  availability?: string;
  functionalDomain?: string;
  linkedInUrl?: string | null;
  profileImageUrl?: string | null;
  approvalStatus?: string;
  rejectionReason?: string | null;
};

const fieldClass = 'mt-1 w-full rounded-md border-gray-300';

const ContributorProfileEditor: React.FC<{
  kind: 'mentors' | 'interviewers';
  open: boolean;
  mode: 'view' | 'edit';
  mentor?: MentorProfile | null;
  interviewer?: InterviewerItem | null;
  saving?: boolean;
  error?: string | null;
  onClose: () => void;
  onSaveMentor?: (profile: MentorProfile) => void;
  onSaveInterviewer?: (profile: InterviewerItem) => void;
}> = ({
  kind,
  open,
  mode,
  mentor,
  interviewer,
  saving,
  error,
  onClose,
  onSaveMentor,
  onSaveInterviewer,
}) => {
  const [mentorForm, setMentorForm] = useState<MentorProfile>({});
  const [interviewerForm, setInterviewerForm] = useState<InterviewerItem | null>(null);
  const readOnly = mode === 'view';

  useEffect(() => {
    setMentorForm(mentor || {});
    setInterviewerForm(interviewer || null);
  }, [mentor, interviewer, open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-start justify-center overflow-y-auto bg-slate-900/40 p-4 pt-16" role="dialog" aria-modal="true">
      <div className="w-full max-w-2xl rounded-2xl bg-white p-5 shadow-xl">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {mode === 'edit' ? 'Edit' : 'View'} {kind === 'mentors' ? 'mentor' : 'interviewer'} profile
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Status: {(kind === 'mentors' ? mentorForm.approvalStatus : interviewerForm?.approvalStatus) || 'Pending'}
            </p>
          </div>
          <button type="button" className="text-sm font-medium text-gray-500 hover:text-gray-800" onClick={onClose}>Close</button>
        </div>
        {error && <p className="mt-3 text-sm text-error-600">{error}</p>}

        {kind === 'mentors' ? (
          <div className="mt-4 grid sm:grid-cols-2 gap-3">
            <Input label="Full name" value={mentorForm.fullName || ''} disabled={readOnly} onChange={(e) => setMentorForm({ ...mentorForm, fullName: e.target.value })} />
            <Input label="Email" value={mentorForm.contactEmail || ''} disabled={readOnly} onChange={(e) => setMentorForm({ ...mentorForm, contactEmail: e.target.value })} />
            <Input label="Phone" value={mentorForm.phoneNumber || ''} disabled={readOnly} onChange={(e) => setMentorForm({ ...mentorForm, phoneNumber: e.target.value })} />
            <Input label="Job title" value={mentorForm.currentJobTitle || ''} disabled={readOnly} onChange={(e) => setMentorForm({ ...mentorForm, currentJobTitle: e.target.value })} />
            <Input label="Years of experience" type="number" value={mentorForm.yearsOfExperience ?? ''} disabled={readOnly} onChange={(e) => setMentorForm({ ...mentorForm, yearsOfExperience: Number(e.target.value) })} />
            <Input label="Primary expertise" value={mentorForm.primaryExpertiseArea || ''} disabled={readOnly} onChange={(e) => setMentorForm({ ...mentorForm, primaryExpertiseArea: e.target.value })} />
            <Input label="Technical skills" value={mentorForm.technicalSkills || ''} disabled={readOnly} onChange={(e) => setMentorForm({ ...mentorForm, technicalSkills: e.target.value })} />
            <Input label="Availability" value={mentorForm.availability || ''} disabled={readOnly} onChange={(e) => setMentorForm({ ...mentorForm, availability: e.target.value })} />
            <Input label="Functional domain" value={mentorForm.functionalDomain || ''} disabled={readOnly} onChange={(e) => setMentorForm({ ...mentorForm, functionalDomain: e.target.value })} />
            <Input label="LinkedIn URL" value={mentorForm.linkedInUrl || ''} disabled={readOnly} onChange={(e) => setMentorForm({ ...mentorForm, linkedInUrl: e.target.value })} />
            <Input label="Profile photo URL" value={mentorForm.profileImageUrl || ''} disabled={readOnly} onChange={(e) => setMentorForm({ ...mentorForm, profileImageUrl: e.target.value })} />
            <label className="sm:col-span-2 text-sm font-medium text-gray-700">
              AI skills / notes
              <textarea className={fieldClass} rows={3} disabled={readOnly} value={mentorForm.aiSkills || ''} onChange={(e) => setMentorForm({ ...mentorForm, aiSkills: e.target.value })} />
            </label>
          </div>
        ) : interviewerForm ? (
          <div className="mt-4 grid sm:grid-cols-2 gap-3">
            <Input label="Full name" value={interviewerForm.fullName || ''} disabled={readOnly} onChange={(e) => setInterviewerForm({ ...interviewerForm, fullName: e.target.value })} />
            <Input label="Email" value={interviewerForm.contactEmail || ''} disabled={readOnly} onChange={(e) => setInterviewerForm({ ...interviewerForm, contactEmail: e.target.value })} />
            <Input label="Phone" value={interviewerForm.phoneNumber || ''} disabled={readOnly} onChange={(e) => setInterviewerForm({ ...interviewerForm, phoneNumber: e.target.value })} />
            <Input label="Job title" value={interviewerForm.currentJobTitle || ''} disabled={readOnly} onChange={(e) => setInterviewerForm({ ...interviewerForm, currentJobTitle: e.target.value })} />
            <Input label="Years of experience" type="number" value={interviewerForm.yearsOfExperience ?? ''} disabled={readOnly} onChange={(e) => setInterviewerForm({ ...interviewerForm, yearsOfExperience: Number(e.target.value) })} />
            <Input label="LinkedIn URL" value={interviewerForm.linkedInUrl || ''} disabled={readOnly} onChange={(e) => setInterviewerForm({ ...interviewerForm, linkedInUrl: e.target.value })} />
            <Input label="Availability" value={interviewerForm.availability || ''} disabled={readOnly} onChange={(e) => setInterviewerForm({ ...interviewerForm, availability: e.target.value })} />
            <Input
              label="Technologies (comma separated)"
              value={(interviewerForm.technologies || []).join(', ')}
              disabled={readOnly}
              onChange={(e) => setInterviewerForm({
                ...interviewerForm,
                technologies: e.target.value.split(',').map((item) => item.trim()).filter(Boolean),
              })}
            />
            <label className="sm:col-span-2 text-sm font-medium text-gray-700">
              Technical skills
              <textarea className={fieldClass} rows={3} disabled={readOnly} value={interviewerForm.technicalSkills || ''} onChange={(e) => setInterviewerForm({ ...interviewerForm, technicalSkills: e.target.value })} />
            </label>
          </div>
        ) : null}

        <div className="mt-5 flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>Close</Button>
          {mode === 'edit' && kind === 'mentors' && (
            <Button type="button" isLoading={saving} onClick={() => onSaveMentor?.(mentorForm)}>Save</Button>
          )}
          {mode === 'edit' && kind === 'interviewers' && interviewerForm && (
            <Button type="button" isLoading={saving} onClick={() => onSaveInterviewer?.(interviewerForm)}>Save</Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContributorProfileEditor;
